import { Box } from "@mui/material";
import * as React from "react";
import {
  Obstacle as ObstacleType,
  ObstacleType as ObstacleTypeType,
} from "../../../types";

type Props = ObstacleType & {
  feetToPixels: number;
  containerWidth?: number;
  containerHeight?: number;
};

export const OBSTACLE_STYLES: Record<
  ObstacleTypeType,
  {
    backgroundColor?: string;
    outlineColor?: string;
    outlineWidth?: string;
    outlineStyle?: string;
  }
> = {
  [ObstacleTypeType.OBSTRUCTION]: {
    backgroundColor: "#d7d7d7",
  },
  [ObstacleTypeType.NO_TOW_AREA]: {
    backgroundColor: "transparent",
    outlineColor: "red",
    outlineWidth: "2px",
    outlineStyle: "dashed",
  },
  [ObstacleTypeType.UNOBSTRUCTIVE]: {
    backgroundColor: "#3c6ce990",
  },
  [ObstacleTypeType.CAUTION]: {
    backgroundColor: "#ECE81A90",
  },
};

/**
 * Check if an obstacle is a custom polygon (not a simple rectangle).
 */
const isPolygonObstacle = (geom: ObstacleType["geom"]): boolean => {
  const coords = geom?.coordinates?.[0];
  if (!coords) return false;

  // Not a rectangle if more than 5 points (4 corners + closing)
  if (coords.length > 5) return true;

  // Check if it's an axis-aligned rectangle
  if (coords.length !== 5) return true;

  const xValues = new Set(coords.map(([x]) => x));
  const yValues = new Set(coords.map(([, y]) => y));

  // Axis-aligned rectangle has exactly 2 unique x values and 2 unique y values
  return !(xValues.size === 2 && yValues.size === 2);
};

/**
 * Detect if an obstacle is a circle by checking if all points are
 * approximately equidistant from the centroid.
 */
const isCircleObstacle = (
  geom: ObstacleType["geom"]
): { isCircle: boolean; center?: { x: number; y: number }; radius?: number } => {
  const coords = geom?.coordinates?.[0];
  if (!coords) return { isCircle: false };

  // Need many points to be a circle (more than 8)
  if (coords.length <= 9) return { isCircle: false };

  // Extract points (excluding closing point)
  const points = coords.slice(0, -1);

  // Calculate centroid
  const sumX = points.reduce((acc, [x]) => acc + x, 0);
  const sumY = points.reduce((acc, [, y]) => acc + y, 0);
  const center = { x: sumX / points.length, y: sumY / points.length };

  // Calculate distances from center to each point
  const distances = points.map(([px, py]) =>
    Math.hypot(px - center.x, py - center.y)
  );

  // Calculate average distance (radius)
  const avgRadius = distances.reduce((sum, d) => sum + d, 0) / distances.length;

  // Check if all distances are within 1% tolerance of average
  const tolerance = avgRadius * 0.01;
  const isCircle = distances.every((d) => Math.abs(d - avgRadius) <= tolerance);

  if (isCircle) {
    return { isCircle: true, center, radius: avgRadius };
  }

  return { isCircle: false };
};

export const Obstacle: React.FC<Props> = ({
  feetToPixels,
  x,
  y,
  type,
  width,
  depth,
  geom,
  containerWidth,
  containerHeight,
}) => {
  const style = OBSTACLE_STYLES[type] || OBSTACLE_STYLES[ObstacleTypeType.OBSTRUCTION];

  // Determine fill and stroke based on type
  const fillColor = style.backgroundColor || "transparent";
  const strokeColor = style.outlineColor || (fillColor !== "transparent" ? fillColor : "#666");
  const strokeDasharray = type === ObstacleTypeType.NO_TOW_AREA ? "5,5" : undefined;

  // Check if this is a circle obstacle first
  if (geom) {
    const circleCheck = isCircleObstacle(geom);
    if (circleCheck.isCircle && circleCheck.center && circleCheck.radius) {
      return (
        <svg
          data-type={`obstacle-${type}-circle`}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: containerWidth ? `${containerWidth}px` : "100%",
            height: containerHeight ? `${containerHeight}px` : "100%",
            pointerEvents: "none",
            zIndex: type === ObstacleTypeType.OBSTRUCTION ? 2 : 1,
          }}
        >
          <circle
            cx={circleCheck.center.x * feetToPixels}
            cy={circleCheck.center.y * feetToPixels}
            r={circleCheck.radius * feetToPixels}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={style.outlineWidth ? parseInt(style.outlineWidth) : 1}
            strokeDasharray={strokeDasharray}
          />
        </svg>
      );
    }
  }

  // Check if this is a polygon obstacle
  if (geom && isPolygonObstacle(geom)) {
    const coords = geom.coordinates[0];

    // Convert coordinates to SVG polygon points string
    const polygonPoints = coords
      .slice(0, -1) // Remove closing point
      .map(([px, py]) => `${px * feetToPixels},${py * feetToPixels}`)
      .join(" ");

    return (
      <svg
        data-type={`obstacle-${type}-polygon`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: containerWidth ? `${containerWidth}px` : "100%",
          height: containerHeight ? `${containerHeight}px` : "100%",
          pointerEvents: "none",
          zIndex: type === ObstacleTypeType.OBSTRUCTION ? 2 : 1,
        }}
      >
        <polygon
          points={polygonPoints}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={style.outlineWidth ? parseInt(style.outlineWidth) : 1}
          strokeDasharray={strokeDasharray}
        />
      </svg>
    );
  }

  // Existing rectangle rendering for backwards compatibility
  return (
    <Box
      data-type={`obstacle-${type}`}
      sx={{
        zIndex: type === ObstacleTypeType.OBSTRUCTION ? 2 : 1,
        position: "absolute",
        top: `${feetToPixels * y}px`,
        left: `${feetToPixels * x}px`,
        width: `${feetToPixels * width}px`,
        height: `${feetToPixels * depth}px`,
        ...style,
      }}
    ></Box>
  );
};
